import {
  Checkbox,
  DatePicker,
  Form,
  Input,
  Space,
  message
} from "antd";
import BoxContainer from "base/ui/components/BoxContainer";
import { observer } from "mobx-react";
// icons
import { ROUTES } from "base/routes/routes";
import CustomSelect from "base/ui/components/CustomSelect";
import PageWrapper from "pages/Components/PageWrapper";
import { SearchForm, SearchItem } from "pages/Components/SearchBox";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SYSTEM_TITLE } from "utils/constants";

import CustomButton from "base/ui/components/Button";
import { Download } from "base/ui/components/Icons";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { BiRightArrowCircle } from "react-icons/bi";
import { CommonModel } from "services/Main/common_services";
import {
  campaignPresenter,
  campaignStore,
  commonPresenter,
  commonStore,
} from "stores/root_store";
import { debounce } from "utils/debounce";
import { useCheckPermission } from "utils/permissionHelper";
import BarChartCampaign from './chartColumn';
import { format } from 'path';

const campaignType = [
  {
    key: "EmailMarketing",
    value: "Email marketing",
  },
  {
    key: "Zalo",
    value: "Zalo",
  },
];

const createAddCampaign = () => {
  return observer(() => {
    //=====================================CHUYỂN NGÔN NGỮ=====================================//
    const { t } = useTranslation();
    // document.title = t('page-title.campaign-manage') + ' - ' + SYSTEM_TITLE;

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const campaignId = searchParams.get("campaignId");

    let pageTitle = "";

    // Nếu có id là chỉnh sửa (xem chi tiết)
    if (campaignId) {
      // Kiểm tra phân quyền
      useCheckPermission("EDIT");
      pageTitle = t("page-title.edit-campaign");
      document.title = pageTitle + " - " + SYSTEM_TITLE;
    } else {
      // Kiểm tra phân quyền
      useCheckPermission("CREATE");
      pageTitle = t("page-title.add-campaign");
      document.title = pageTitle + " - " + SYSTEM_TITLE;
    }

    const [form] = Form.useForm();
    // Dropdown nội dung
    const [listContent, setListContent] = useState<CommonModel[]>([]);
    // Dropdown customer segment
    const [listCS, setListCS] = useState<CommonModel[]>([]);

    const contentID = Form.useWatch("contentId", form);
    const segmentId = Form.useWatch("customerSegmentationId", form);
    const ngayLapTuc = Form.useWatch("isImmediately", form);

    // lưu / lưu và tiếp tục
    const [isContinue, setContinue] = useState(false);

    // Lấy data dropdown nội dung
    const getDataDropdownContent = async (key?: string) => {
      await commonPresenter.getListContents(commonStore, key);
      if (commonStore.commonRespone.isSuccess) {
        setListContent(commonStore.commonRespone.data);
      }
    };

    const debounceContent = useCallback(
      debounce(getDataDropdownContent, 300),
      []
    );
    // Lấy data danh sách customer segment
    const getDataDropdownCustomerSegment = async (key?: string) => {
      await commonPresenter.getListCustomerSegment(commonStore, key);
      if (commonStore.commonRespone.isSuccess) {
        setListCS(commonStore.commonRespone.data);
      }
    };
    const debounceCustomerSegment = useCallback(
      debounce(getDataDropdownCustomerSegment, 300),
      []
    );

    useEffect(() => {
      (async () => {
        getDataDropdownContent();
        getDataDropdownCustomerSegment();

        // lấy data detail của chiến dịch để fill vào
        if (campaignId) {
          await campaignPresenter.getCampaignDetails(campaignStore, campaignId);
          if (campaignStore.campaignDetails.isSuccess) {
            const data = cloneDeep(campaignStore.campaignDetails.data);
            await getDataDropdownContent(data?.contentName);
            await getDataDropdownCustomerSegment(
              data?.customerSegmentationName
            );
            form.setFieldsValue({
              ...data,
              scheduledToStart: dayjs(data?.scheduledToStart),
            });
          } else {
            message.error(campaignStore.campaignDetails.message);
          }

          // get data biểu đồ cột
          await campaignPresenter.getDataBarChartReport(campaignStore, campaignId);

        }
      })();
    }, []);

    return (
      <PageWrapper
        breadcrumb={[
          {
            text: t("page-title.campaign-manage"),
            to: ROUTES.CAMPAIGN.CAMPAIGN_MANAGE.LINK,
          },
          {
            text: pageTitle,
          },
        ]}
        extras={
          <>
            <CustomButton
              loading={loading}
              onClick={(e) => {
                form.submit();
                setContinue(false);
              }}
              type="default"
              icon={<Download className="!fill-black" />}
            >
              {t("btn.save")}
            </CustomButton>
            <CustomButton
              loading={loading}
              onClick={() => {
                form.submit();
                setContinue(true);
              }}
              type="primary"
              icon={<Download className="!fill-white" />}
            >
              Lưu và tiếp tục
            </CustomButton>
          </>
        }
      >
        {/* Table */}
        <BoxContainer fullHeight>
          <SearchForm
            form={form}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 16 }}
            labelAlign="right"
            onFinish={async (fieldsValue) => {
              const values = {
                ...fieldsValue,
                scheduledToStart: fieldsValue["scheduledToStart"].toJSON(),
                campaignId: campaignId ? campaignId : undefined

              };
              setLoading(true);
              const result = campaignId
                ? await campaignPresenter.editCampaign(values)
                : await campaignPresenter.createCampaign(values);
              setLoading(false);
              if (result.isSuccess) {
                message.success(result.message);
                if (isContinue) {
                  // nếu đang chỉnh sửa thì không làm gì
                  if (!campaignId) form.resetFields();
                } else {
                  navigate(ROUTES.CAMPAIGN.CAMPAIGN_MANAGE.LINK);
                }
              } else {
                message.error(result.message);
              }
            }}
          >
            <div className="col-span-2 flex flex-col gap-y-2">
              {/* Mã chiến dịch */}
              <SearchItem
                hidden={campaignId ? false : true}
                name={"campaignCode"}
                label={t("event.campaign-code")}
              >
                <Input readOnly disabled />
              </SearchItem>
              {/* Tên chiến dịch */}
              <SearchItem
                rules={[{ required: true }]}
                name={"campaignName"}
                label={t("event.campaign-name")}
              >
                <Input />
              </SearchItem>

              {/* Mô tả */}
              <SearchItem name={"description"} label={t("segment.mo-ta")}>
                <Input.TextArea rows={3} />
              </SearchItem>

              {/* Campaign code */}
              {/* <SearchItem name={'campaignCode'} label={t('event.campaign-code')}>
                <Input />
              </SearchItem> */}

              {/* Chọn nôi dung */}
              <SearchItem
                rules={[{ required: true, message: "" }]}
                name={"contentId"}
                label={t("campaign.choose-content")}
              >
                <Space.Compact block>
                  <Form.Item
                    rules={[{ required: true, message: "Vui lòng nhập nội dung" }]}
                    name={"contentId"}
                    className="!m-0 w-full"
                  >
                    <CustomSelect
                      options={listContent}
                      fieldNames={{ label: "value", value: "key" }}
                      onSearch={(value) => {
                        debounceContent(value ? value : undefined);
                      }}
                      onClear={() => getDataDropdownContent()}
                      filterOption={false}
                      showSearch
                      allowClear
                    />
                  </Form.Item>
                  <div className="group w-8 h-8 border rounded-sm border-l-0 rounded-l-none border-background hover:bg-primary hover:border-primary">
                    <Link
                      target={contentID ? "_blank" : undefined}
                      to={
                        contentID
                          ? ROUTES.CAMPAIGN.CONTENT_MANAGE.DETAIL.LINK +
                          "?ContentId=" +
                          contentID
                          : ""
                      }
                      className="w-full h-full flex justify-center items-center"
                    >
                      <BiRightArrowCircle
                        className="text-black group-hover:text-white"
                        fontSize={16}
                      />
                    </Link>
                  </div>
                </Space.Compact>
              </SearchItem>
              {/* Chọn segment */}
              <SearchItem
                rules={[{ required: true, message: "" }]}
                name={"customerSegmentationId"}
                label={t("campaign.choose-segment")}
              >
                <Space.Compact block>
                  <Form.Item
                    rules={[{ required: true }]}
                    name={"customerSegmentationId"}
                    className="!m-0 w-full"
                  >
                    <CustomSelect
                      options={listCS}
                      fieldNames={{ label: "value", value: "key" }}
                      onSearch={(value) => {
                        debounceCustomerSegment(value ? value : undefined);
                      }}
                      onClear={() => getDataDropdownCustomerSegment()}
                      filterOption={false}
                      notFoundContent
                      showSearch
                      allowClear
                    />
                  </Form.Item>
                  <div className="group w-8 h-8 border rounded-sm border-l-0 rounded-l-none border-background hover:bg-primary hover:border-primary">
                    <Link
                      target={segmentId ? "_blank" : undefined}
                      to={
                        segmentId
                          ? ROUTES.CUSTOMER.CUSTOMER_SEGMENTATION.DETAIL.LINK +
                          "?type=detail&tabName=" +
                          `${encodeURIComponent(
                            listCS.find((cs) => cs.key === segmentId)?.value +
                            ""
                          )}&tabId=` +
                          segmentId
                          : ""
                      }
                      className="w-full h-full flex justify-center items-center"
                    >
                      <BiRightArrowCircle
                        className="text-black group-hover:text-white"
                        fontSize={16}
                      />
                    </Link>
                  </div>
                </Space.Compact>
              </SearchItem>
              {/* Gửi ngay lập tức*/}
              <SearchItem
                name={"isImmediately"}
                label={t("campaign.send-now")}
                valuePropName="checked"
              >
                <Checkbox
                  onChange={(e) => {
                    // nếu check thì set lịch gửi là thời gian hiện tại
                    if (!campaignId && e.target.checked) {
                      form.setFieldValue('scheduledToStart', dayjs())
                    }
                  }}
                  disabled={campaignId ? true : false}></Checkbox>
              </SearchItem>
              {/* Lịch gửi */}
              <SearchItem
                rules={[{ required: true }]}
                name={"scheduledToStart"}
                label={t("event.sent-date")}
              >
                <DatePicker
                  className="w-full"
                  disabled={campaignId ? true : ngayLapTuc ? true : false}
                  showTime

                  format={"DD/MM/YYYY HH:mm A"}
                />
              </SearchItem>

              {/* Loại chiến dịch */}
              <SearchItem
                rules={[{ required: true }]}
                name={"sendType"}
                label={t("campaign.campaign-type")}
              >
                <CustomSelect
                  options={campaignType}
                  fieldNames={{ label: "value", value: "key" }}
                  allowClear
                />
              </SearchItem>
            </div>
          </SearchForm>


          {
            campaignId && (
              <div className='mt-4'>
                <BarChartCampaign
                  dataChart={campaignStore.columnChart.data}
                  // dataChart={
                  //   [
                  //     {
                  //       name: "Tổng KH",
                  //       value: 7
                  //     },
                  //     {
                  //       name: "Gửi thành công",
                  //       value: 0
                  //     },
                  //     {
                  //       name: "Gửi thất bại",
                  //       value: 0
                  //     },
                  //     {
                  //       name: "Được mở",
                  //       value: 0
                  //     }
                  //   ]
                  // }
                  title={'Báo cáo'}
                />
              </div>
            )
          }

        </BoxContainer>



      </PageWrapper>
    );
  });
};

export default createAddCampaign;
